import { GoogleGenAI } from "@google/genai";

// --- 1. DATA PREPARATION (Deep Context) ---
const getAnalysisSummary = (analysis) => {
  // Convert complex objects to a streamlined JSON string for the LLM
  return {
    kpi_generali: analysis.kpi_portafoglio,

    // FIX: Provide explicit Year-over-Year claims count (ATR) as the source of truth for "How many claims?"
    storico_sinistri_reale_per_anno: analysis.analisi_annuale_atr.map((a) => ({
      anno: a.Anno,
      sinistri_accaduti: a.Numero_Sinistri_Totali, // This is the REAL number of accidents in that year
      veicoli_osservati: a.Veicoli_Osservati,
      frequenza: a.Frequenza_Totale,
    })),

    // FIX: Rename monthly data to clearly indicate it is Underwriting/Renewal data, not Occurrence data
    dettaglio_polizze_per_mese_decorrenza: analysis.freq_mensile.map((m) => ({
      mese_decorrenza: m.Mese,
      nuove_polizze_emesse: m.Esposizione,
      // Labeling this as "Accumulated History" prevents AI from thinking these are new accidents
      sinistri_storici_accumulati_su_queste_polizze: m.Totale_Sinistri,
      loss_ratio_stimato: m.Loss_Ratio_percent,
    })),

    top_compagnie: analysis.stats_compagnia.slice(0, 20),
    mix_veicoli: analysis.stats_tipo_veicolo,
    previsioni_future: analysis.forecast,
    dati_tecnici: {
      potenza_media: analysis.ml_enriched_data?.dist_kw.avg,
      cilindrata_media: analysis.ml_enriched_data?.dist_cilindrata.avg,
      eta_media_veicoli: analysis.ml_enriched_data?.dist_anzianita_veicolo.avg,
      percorrenza_media: analysis.ml_enriched_data?.stima_percorrenza_media,
    },
    distribuzione_geografica: analysis.dist_provincia,
  };
};

// --- 2. MAIN CHAT FUNCTION (Active Agent) ---
export const askAI = async (analysis, userQuestion, history) => {
  if (!process.env.REACT_APP_GEMINI_API_KEY) {
    return JSON.stringify({
      answer:
        "Errore: API Key non configurata. Configura l'ambiente per usare l'AI.",
      proposed_action: null,
    });
  }

  const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY });

  // Prepare data context
  const dataContext = JSON.stringify(getAnalysisSummary(analysis));

  // Prepare history (limit to last 10 messages to save context)
  const recentHistory = history
    .slice(-10)
    .map((msg) => `${msg.role === "user" ? "Utente" : "AI"}: ${msg.text}`)
    .join("\n");

  const systemInstruction = `
      SEI: "Senior Actuary AI", l'analista tecnico di InsurAnalytics Pro.
      
      IL TUO OBIETTIVO: 
      1. Analizzare i dati forniti per rispondere a domande complesse sul portafoglio.
      2. Agire come "Agente Attivo": se l'utente chiede simulazioni o modifiche, proponi un'azione concreta.

      IMPORTANTE - GUIDA LETTURA DATI (DATA INTERPRETATION RULES):
      1. Per domande sul NUMERO DI SINISTRI nel tempo (es. "Quanti sinistri nel 2025?", "Andamento sinistri"), USA SOLO l'oggetto "storico_sinistri_reale_per_anno".
         - Esempio: Se "storico_sinistri_reale_per_anno" dice che nel 2025 ci sono 29 sinistri, RISPONDI 29.
      
      2. L'oggetto "dettaglio_polizze_per_mese_decorrenza" mostra le polizze raggruppate per mese di rinnovo/emissione.
         - Il campo "sinistri_storici_accumulati_su_queste_polizze" è la somma di TUTTI i sinistri passati (di 5-10 anni) delle polizze che rinnovano in quel mese.
         - ATTENZIONE: Se vedi 160 sinistri a Giugno 2025 in questa tabella, NON dire che ci sono stati 160 incidenti a Giugno! Significa che le auto che rinnovano a Giugno hanno avuto 160 incidenti nella loro vita passata.
      
      3. Se l'utente chiede PREVISIONI future, usa "previsioni_future".

      ACCESSO AI DATI:
      Hai accesso ai KPI, ai trend annuali e mensili (underwriting), alle statistiche per compagnia e veicolo.
      
      FORMATO RISPOSTA (JSON STRICT MODE):
      Devi rispondere SEMPRE E SOLO con un oggetto JSON valido.
      
      Struttura JSON:
      {
        "answer": "Testo della risposta. Usa markdown. Sii professionale. Cita i dati corretti.",
        "proposed_action": {
          "type": "UPDATE_COST" | "UPDATE_PREMIUMS" | "UPDATE_DEDUCTIBLE" | null,
          "value": number,
          "reason": "Etichetta bottone (es. Imposta Costo a 3.000€)"
        }
      }

      REGOLE PER "PROPOSED_ACTION":
      - Usa "proposed_action" se l'utente chiede esplicitamente una simulazione o modifica ("Simula costo 3000", "Alza premi 10%").
      - Altrimenti null.

      DATI PORTAFOGLIO ATTUALI:
      ${dataContext}
    `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: [
        {
          role: "user",
          parts: [
            {
              text: `STORICO:\n${recentHistory}\n\nDOMANDA UTENTE:\n${userQuestion}`,
            },
          ],
        },
      ],
      config: {
        systemInstruction: systemInstruction,
        responseMimeType: "application/json",
        temperature: 0.1, // Lower temperature to strictly follow data interpretation rules
      },
    });

    return (
      response.text ||
      JSON.stringify({
        answer: "Non ho potuto generare una risposta.",
        proposed_action: null,
      })
    );
  } catch (error) {
    console.error("Gemini API Error:", error);
    return JSON.stringify({
      answer:
        "Si è verificato un errore tecnico durante l'elaborazione della risposta.",
      proposed_action: null,
    });
  }
};

// --- 3. REPORT GENERATION (Static) ---
export const generateAIReport = async (analysis) => {
  if (!process.env.REACT_APP_GEMINI_API_KEY) return "API Key mancante.";

  const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY });
  const summary = getAnalysisSummary(analysis);

  const prompt = `
    Analizza i seguenti dati tecnici di un portafoglio assicurativo RCA (Auto) e genera un Report Esecutivo in Markdown.
    
    IMPORTANTE: Per i trend dei sinistri, fai riferimento a 'storico_sinistri_reale_per_anno'.
    
    Dati: ${JSON.stringify(summary)}
    
    Struttura del Report:
    1. **Executive Summary**: Giudizio sintetico sulla salute del portafoglio (Combined Ratio e Profitto).
    2. **Analisi Tecnica**: Commenta il Loss Ratio (${
      analysis.kpi_portafoglio.loss_ratio_percent
    }%) e la Frequenza.
    3. **Trend e Previsioni**: Cosa ci aspettiamo nei prossimi 12 mesi?
    4. **Focus Compagnie**: Quali sono le compagnie più performanti?
    5. **Raccomandazioni**: 3 azioni correttive basate sui dati.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      contents: prompt,
    });
    return response.text || "Errore generazione report.";
  } catch (error) {
    return "Errore API Report.";
  }
};

// --- 4. ML SIMULATION (Level 3) ---
export const generateMLPrediction = async (analysis) => {
  if (!process.env.REACT_APP_GEMINI_API_KEY) return null;

  const ai = new GoogleGenAI({ apiKey: process.env.REACT_APP_GEMINI_API_KEY });

  const context = {
    tot_veicoli: analysis.kpi_portafoglio.tot_polizze,
    frequenza_attuale: analysis.kpi_portafoglio.frequenza_percent,
    arricchimento_dati: analysis.ml_enriched_data,
  };

  const prompt = `
        Sei un AI Machine Learning Engineer esperto in Attuariato. 
        Stai simulando un modello XGBoost per questo portafoglio.
        
        CONTESTO DATI: 
        ${JSON.stringify(context)}

        Compito:
        Genera una previsione ML simulata e suggerimenti di data enrichment.
        
        Rispondi ESCLUSIVAMENTE con questo JSON:
        {
            "predicted_frequency_poisson": numero,
            "predicted_severity_gamma": numero,
            "suggested_pure_premium": numero,
            "shap_values": [{ "feature": string, "impact": "high"|"medium"|"low", "direction": "positive"|"negative", "description": string }],
            "missing_data_roadmap": [{ "category": string, "field_name": string, "reason": string }],
            "model_confidence_score": numero (0-100)
        }
    `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-2.5-flash",
      config: { responseMimeType: "application/json" },
      contents: prompt,
    });
    const text = response.text;
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error("ML Error:", error);
    return null;
  }
};
